import React, { useState } from 'react'
import { Drawer, Box, IconButton, Typography, useTheme, useMediaQuery, Tabs, Tab } from '@mui/material'
import Close from '@mui/icons-material/Close'
import { styled } from '@mui/material/styles'
import PrescriptionViewer from './PrescriptionViewer'

const DrawerHeader = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: theme.spacing(2, 3),
  borderBottom: `1px solid ${theme.palette.divider}`,
  background: 'linear-gradient(135deg, rgba(67, 56, 202, 0.05) 0%, rgba(99, 102, 241, 0.05) 100%)',
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(1.5, 2),
  },
}))

const DrawerContent = styled(Box)(({ theme }) => ({
  width: '100%',
  height: '100%',
  overflowY: 'auto',
  '&::-webkit-scrollbar': {
    width: '8px',
  },
  '&::-webkit-scrollbar-track': {
    background: 'transparent',
  },
  '&::-webkit-scrollbar-thumb': {
    background: 'rgba(0, 0, 0, 0.2)',
    borderRadius: '4px',
    '&:hover': {
      background: 'rgba(0, 0, 0, 0.3)',
    },
  },
}))

const TabPanel = styled(Box)(({ theme }) => ({
  padding: theme.spacing(0),
  height: '100%',
  overflow: 'auto',
}))

export default function InterventionActionDrawer({ open, onClose, intervention }) {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))
  const [activeTab, setActiveTab] = useState(0)

  if (!intervention) {
    return null
  }

  const getDrawerTitle = () => {
    if (intervention?.patient?.name && intervention?.mentor?.name) {
      return `${intervention.patient.name} - ${intervention.mentor.name}`
    }
    return 'Intervention Details'
  }

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue)
  }

  return (
    <Drawer
      anchor={isMobile ? 'bottom' : 'right'}
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: isMobile ? '100%' : 800,
          maxWidth: isMobile ? '100%' : '90vw',
          maxHeight: isMobile ? '90vh' : '100vh',
          borderTopLeftRadius: isMobile ? theme.shape.borderRadius * 2 : 0,
          borderTopRightRadius: isMobile ? theme.shape.borderRadius * 2 : 0,
          boxShadow: isMobile
            ? '0 -4px 20px rgba(0, 0, 0, 0.15)'
            : '0 4px 20px rgba(0, 0, 0, 0.15)',
          display: 'flex',
          flexDirection: 'column',
        },
      }}
      ModalProps={{
        keepMounted: false,
      }}
    >
      <DrawerHeader>
        <Typography variant="h6" fontWeight={600} sx={{ flex: 1, mr: 2 }}>
          {getDrawerTitle()}
        </Typography>
        <IconButton
          onClick={onClose}
          sx={{
            color: 'text.primary',
            '&:hover': {
              backgroundColor: 'action.hover',
            },
          }}
        >
          <Close />
        </IconButton>
      </DrawerHeader>

      <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <Tabs value={activeTab} onChange={handleTabChange} aria-label="intervention action tabs">
          <Tab label="View Prescription" />
          {/* Future tabs can be added here */}
        </Tabs>
      </Box>

      <DrawerContent sx={{ flex: 1, overflow: 'hidden' }}>
        <TabPanel role="tabpanel" hidden={activeTab !== 0}>
          {activeTab === 0 && (
            <PrescriptionViewer
              bookedEventId={intervention?.bookedEventId}
              patientId={intervention?.patient?.patientId}
              bookedEventSlug={intervention?.bookedEventSlug}
              intervention={intervention}
            />
          )}
        </TabPanel>
      </DrawerContent>
    </Drawer>
  )
}

